import Conviva from '@convivainc/conviva-js-coresdk';
import { Accessibility, Device, Lifecycle, Localization } from '@firebolt-js/sdk';
import { ClientType, ColorSpace } from '@mlbtv-clients/services';
import { WIRED, WIRELESS } from 'analytics/conviva/constants';
import { releaseConvivaAnalyticsSingleton } from 'analytics/conviva/hooks/useConviva/convivaAnalyticsSingleton';
import { EASTERN_TZ } from 'constants/date';
import { MODEL_YEAR_UNKNOWN } from 'constants/platforms';
import { US_COUNTRY_CODE } from 'constants/userInfo';
import { ModelTier } from 'hooks/useDeviceModelTier/types';
import { getModelTier } from 'hooks/useDeviceModelTier/utils';
import { EPGErrors } from 'services/epg/constants';
import fallback from 'store/config/fallback.json';
import { IFA } from 'store/deviceSettings';
import { v4 as uuidv4 } from 'uuid';
import { ClientTypeMap, DeviceCategoryMap, Distributor, MetaDataTypeMap } from './deviceTypeMaps';
import { DeviceMetadata, DeviceTypes, Version } from './types';

const { EPG_SERVICE_ERROR } = EPGErrors;

const {
  Constants: { DeviceMetadata: DeviceMetadataConstants },
} = Conviva;

const {
  BRAND,
  CATEGORY,
  MANUFACTURER,
  MODEL,
  OS_NAME,
  SCREEN_RESOLUTION_HEIGHT,
  SCREEN_RESOLUTION_WIDTH,
  TYPE,
} = DeviceMetadataConstants;

//let Firebolt know the app is initialized (required)
Lifecycle.ready();

export const getClientType = async () => {
  const distributor = await getFireboltString(Device.distributor, 'Device.distributor', 'comcast');
  return (
    ClientTypeMap[distributor as keyof typeof Distributor] || ClientType.COMCAST_FLEX // default to COMCAST_FLEX if distributor is unknown
  );
};

export const getDeviceOSVersion = async (defaultOS: string) => {
  const version: Version = await getDeviceVersionInfo();
  return version.os?.readable || defaultOS;
};

// Generated once per app session so ad requests keep a stable rdid when Firebolt provides no IFA.
let sessionDuid = '';
const getSessionDuid = () => {
  if (!sessionDuid) {
    sessionDuid = uuidv4();
  }
  return sessionDuid;
};

export const getDeviceAdParams = (adParams: Record<string, string>, ifaParams?: IFA) => {
  return {
    ...adParams,
    is_lat: ifaParams?.LMT === 1,
    rdid: ifaParams?.IFA || getSessionDuid(),
  };
};

export const getDeviceCaptionStyle = async () => ``;

export const getDeviceCountryCode = async () =>
  getFireboltString(Localization.countryCode, 'Localization.countryCode', US_COUNTRY_CODE);

export const getDeviceConnectionType = async () => {
  const network: Device.NetworkInfoResult = await Device.network();
  return network?.type === Device.NetworkType.WIFI ? WIRELESS : WIRED;
};

export const getDeviceExitApp = () => {
  releaseConvivaAnalyticsSingleton();
  try {
    Lifecycle.close(Lifecycle.CloseReason.USER_EXIT);
    Lifecycle.finished();
  } catch (e) {
    console.error(`Error on deviceExit from ${__PLATFORM__} - firebolt`, e);
  }
};

// comcast does not show exit confirmation
export const getDeviceExitConfirmation = getDeviceExitApp;

export const getDeviceId = async (deviceId: string) => {
  let knownDeviceId = deviceId;
  try {
    knownDeviceId = await Device.id();
    if (typeof knownDeviceId !== 'string' || knownDeviceId.length === 0) {
      // if not a string (Flex xi6 returning {}) or blank, fallback to the uid
      knownDeviceId = await Device.uid();
    }
  } catch (e) {
    console.warn(`Failed to retrieve deviceId from ${__PLATFORM__} - firebolt`, e);
  }
  return knownDeviceId;
};

export const getDeviceIdType = () => 'dpid';

export const getDeviceKeyboardIsOpen = async () => false;

export const getDeviceMetadata = async (deviceMetadata: DeviceMetadata) => {
  const { deviceBrand, deviceManufacturer, deviceModel, operatingSystemName } = deviceMetadata;

  const deviceType: DeviceTypes = await getDeviceType();
  const [screenWidth, screenHeight] = await getDeviceVideoResolution();

  const metadata = {
    [BRAND]: await getFireboltString(Device.make, 'Device.make', deviceBrand),
    [CATEGORY]: DeviceCategoryMap[deviceType],
    [MANUFACTURER]: deviceManufacturer,
    [MODEL]: await generateModelName(deviceModel),
    [OS_NAME]: await getDeviceOSVersion(operatingSystemName),
    [SCREEN_RESOLUTION_HEIGHT]: screenHeight,
    [SCREEN_RESOLUTION_WIDTH]: screenWidth,
    [TYPE]: MetaDataTypeMap[deviceType],
  };

  return metadata;
};

// https://baseball.atlassian.net/wiki/spaces/WS/pages/4985061400/Xumo+Model+Tiers
export const getDeviceModelTier = async (modelTiers: Record<string, ModelTier>) => {
  const sku = await getDeviceSKU('');
  return getModelTier(modelTiers, sku);
};

export const getDeviceModelYear = async () => MODEL_YEAR_UNKNOWN;

export const getDevicePlaybackCapabilities = async () => {
  try {
    const negotiatedHdrFormats = await Device.hdr();
    return {
      colorSpace: [negotiatedHdrFormats.hdr10 ? ColorSpace.HDR : ColorSpace.SDR],
    };
  } catch (e) {
    console.warn(`Failed to retrieve Device.hdr()`, e);
    return { colorSpace: [ColorSpace.SDR] };
  }
};

export const getDeviceHasSpinner = async (
  hasSpinner: typeof fallback.featureFlags.animations.hasSpinner,
) => !hasSpinner.hasAllModelsDisabled;

export const getFireboltString = async <T>(
  functionToCall: () => Promise<string>,
  logName: string,
  defaultValue: T,
) => {
  try {
    const fireboltValue = await functionToCall();
    return fireboltValue && typeof fireboltValue === 'string' ? fireboltValue : defaultValue; //Flex 1 device fix
  } catch (e) {
    console.warn(
      `Failed to retrieve ${logName} from ${__PLATFORM__} - firebolt, using default value: ${defaultValue}`,
      e,
    );
  }
  return defaultValue;
};

const generateModelName = async (defaultValue: string) => {
  const model = await getDeviceModel(defaultValue);
  const sku = await getDeviceSKU('');
  return `${model} ${sku}`;
};

export const getDeviceModel = async (defaultValue: string) => {
  return await getFireboltString(Device.model, 'Device.model', defaultValue);
};

export const getDeviceSKU = async (defaultValue: string) => {
  return await getFireboltString(Device.sku, 'Device.sku', defaultValue);
};

const getDeviceVersionInfo = async () => {
  try {
    return await Device.version();
  } catch (e) {
    console.warn(`Failed to retrieve Device.version() from ${__PLATFORM__} - firebolt`, e);
  }
  return {
    debug: 'Comcast device version information unavailable',
  };
};

const getDeviceVideoResolution = async () => {
  try {
    return await Device.videoResolution();
  } catch (e) {
    console.warn(`Failed to retrieve Device.videoResolution() from ${__PLATFORM__} - firebolt`, e);
  }
  return [window.screen.width, window.screen.height];
};

//get the device type, which is used as a key to find conviva values
export const getDeviceType = async () => {
  const deviceType = await getFireboltString(Device.type, 'Device.type', 'web');
  return (Object.values(DeviceTypes) as string[]).includes(deviceType)
    ? (deviceType as DeviceTypes)
    : DeviceTypes.WEB;
};

export const getDeviceTimeZone = () => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  if (!timeZone) {
    throw new Error(`${EPG_SERVICE_ERROR} Time zone not found. Falling back to ${EASTERN_TZ}`);
  }

  return timeZone;
};

export const getDeviceTTSEnabled = async () => {
  const ttsSettings: Accessibility.VoiceGuidanceSettings =
    await Accessibility.voiceGuidanceSettings();
  if (ttsSettings) {
    return ttsSettings.enabled;
  }
};
